// Champs d'un objet ou d'une étape, LUS DANS LE SCHÉMA Zod (Studio, lot 5) :
// l'inspecteur du Studio dessine ses formulaires à partir d'ici, toujours
// d'accord avec le format (un champ ajouté au schéma apparaît dans le
// Studio sans autre code). Pur.

import { z } from 'zod';
import { DISPLAY_TEXT } from './schema/primitives';
import { jrafObjectSchema, type JrafObject } from './schema/objects';
import { jrafStepSchema, type JrafAction } from './schema/steps';

export type FieldKind =
  /** texte affiché (`$…$` permis) */
  | 'display'
  /** LaTeX sans `$` */
  | 'tex'
  | 'string'
  | 'number'
  | 'int'
  /** nombre ou expression des paramètres (« 260 / a ») */
  | 'numExpr'
  | 'boolean'
  | 'enum'
  /** couleur de la palette ou #rrggbb */
  | 'color'
  /** couleur ou « none » */
  | 'paint'
  /** { x, y } */
  | 'point'
  /** objet(s) visé(s) par une étape */
  | 'target'
  /** identifiant d'un autre élément (groupe parent, étape `with`, outil, ressource) */
  | 'ref'
  /** sous-objet (style, cadre, état de départ) */
  | 'object'
  /** liste (choix d'une question, noms, points d'un polygone) */
  | 'list'
  /** comportement dans la vidéo (`exportBehavior`) */
  | 'variant';

export interface FieldSpec {
  key: string;
  kind: FieldKind;
  label: string;
  optional: boolean;
  default?: unknown;
  options?: readonly string[];
  min?: number;
  max?: number;
  /** sous-champs (`object`), ou modèle d'un élément (`list`) */
  fields?: FieldSpec[];
  item?: FieldSpec;
  /** `ref` : ce que l'identifiant désigne */
  refTo?: 'group' | 'step' | 'tool' | 'asset' | 'sentence' | 'choice';
  /** `variant` : formes possibles (`exportBehavior`) */
  variants?: { value: string; fields: FieldSpec[] }[];
}

/** Libellés des champs (français, ceux du Studio). */
export const FIELD_LABELS: Record<string, string> = {
  id: 'Identifiant',
  parent: 'Groupe',
  initial: 'Au départ',
  visible: 'Visible',
  opacity: 'Opacité',
  mentions: 'Noms (la voix les éclaire)',
  x: 'x',
  y: 'y',
  width: 'Largeur',
  height: 'Hauteur',
  rx: 'Arrondi',
  ry: 'Rayon y',
  cx: 'Centre x',
  cy: 'Centre y',
  r: 'Rayon',
  x1: 'x départ',
  y1: 'y départ',
  x2: 'x arrivée',
  y2: 'y arrivée',
  head: 'Pointe',
  points: 'Sommets',
  d: 'Tracé (M L H V Q C A Z)',
  label: 'Étiquette',
  labelPosition: 'Place de l’étiquette',
  at: 'Position',
  text: 'Texte',
  tex: 'Formule (LaTeX)',
  size: 'Taille',
  align: 'Alignement',
  weight: 'Graisse',
  color: 'Couleur',
  maxWidth: 'Largeur max (retour à la ligne)',
  box: 'Cadre',
  expr: 'Valeur',
  decimals: 'Décimales',
  prefix: 'Avant',
  suffix: 'Après',
  asset: 'Image',
  alt: 'Description',
  style: 'Style',
  fill: 'Remplissage',
  stroke: 'Trait',
  strokeWidth: 'Épaisseur',
  dash: 'Pointillés',
  padding: 'Marge',
  radius: 'Arrondi',
  with: 'En même temps que',
  atSentence: 'Au début de la phrase',
  delayMs: 'Décalage (ms)',
  durationMs: 'Durée (ms)',
  easing: 'Mouvement',
  target: 'Objet',
  effect: 'Effet',
  dx: 'Décalage x',
  dy: 'Décalage y',
  scale: 'Échelle',
  origin: 'Centre',
  angle: 'Angle (°)',
  to: 'Nouvelle écriture (LaTeX)',
  flash: 'Éclair',
  prompt: 'Consigne',
  choices: 'Choix',
  correct: 'Bonne réponse',
  hint: 'Indice',
  explanation: 'Explication',
  exportBehavior: 'Dans la vidéo',
  toolId: 'Outil',
  afterMs: 'Réponse montrée après (ms)',
  mode: 'Mode',
};

const TEX_KEYS = new Set(['tex', 'to']);
const REF_KEYS: Record<string, FieldSpec['refTo']> = {
  parent: 'group',
  with: 'step',
  toolId: 'tool',
  asset: 'asset',
  atSentence: 'sentence',
  correct: 'choice',
};
/** Champs que le Studio ne montre pas (fixés par le type, l'action ou l'outil). */
const HIDDEN = new Set(['type', 'action', 'id']);

type AnyZod = z.ZodTypeAny;

function unwrap(s: AnyZod): { inner: AnyZod; optional: boolean; default?: unknown } {
  let optional = false;
  let dflt: unknown;
  let cur: AnyZod = s;
  for (let guard = 0; guard < 10; guard++) {
    if (cur instanceof z.ZodOptional || cur instanceof z.ZodNullable) {
      optional = true;
      cur = cur.unwrap() as AnyZod;
    } else if (cur instanceof z.ZodDefault) {
      optional = true;
      dflt = cur._def.defaultValue();
      cur = cur._def.innerType as AnyZod;
    } else if (cur instanceof z.ZodEffects) {
      cur = cur.innerType() as AnyZod;
    } else break;
  }
  return { inner: cur, optional, ...(dflt !== undefined ? { default: dflt } : {}) };
}

function numberBounds(n: z.ZodNumber): { min?: number; max?: number; int: boolean } {
  let min: number | undefined;
  let max: number | undefined;
  let int = false;
  for (const c of n._def.checks) {
    if (c.kind === 'min') min = c.value;
    else if (c.kind === 'max') max = c.value;
    else if (c.kind === 'int') int = true;
  }
  return { ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}), int };
}

function describe(key: string, schema: AnyZod): FieldSpec {
  const { inner, optional, default: dflt } = unwrap(schema);
  const base = {
    key,
    label: FIELD_LABELS[key] ?? key,
    optional,
    ...(dflt !== undefined ? { default: dflt } : {}),
  };
  const ref = REF_KEYS[key];
  if (ref) {
    const extra = inner instanceof z.ZodNumber ? numberBounds(inner) : {};
    return { ...base, kind: 'ref', refTo: ref, ...extra };
  }
  if (inner instanceof z.ZodString) {
    if (inner.description === DISPLAY_TEXT) return { ...base, kind: 'display' };
    return { ...base, kind: TEX_KEYS.has(key) ? 'tex' : 'string' };
  }
  if (inner instanceof z.ZodNumber) {
    const b = numberBounds(inner);
    return {
      ...base,
      kind: b.int ? 'int' : 'number',
      ...(b.min !== undefined ? { min: b.min } : {}),
      ...(b.max !== undefined ? { max: b.max } : {}),
    };
  }
  if (inner instanceof z.ZodBoolean) return { ...base, kind: 'boolean' };
  if (inner instanceof z.ZodEnum) return { ...base, kind: 'enum', options: inner.options };
  if (inner instanceof z.ZodUnion) {
    const opts = inner.options as AnyZod[];
    if (opts.some((o) => o instanceof z.ZodArray)) return { ...base, kind: 'target' };
    if (opts.some((o) => o instanceof z.ZodLiteral && o.value === 'none'))
      return { ...base, kind: 'paint' };
    if (opts.some((o) => o instanceof z.ZodEnum)) return { ...base, kind: 'color' };
    if (opts.some((o) => o instanceof z.ZodNumber)) return { ...base, kind: 'numExpr' };
  }
  if (inner instanceof z.ZodDiscriminatedUnion) {
    const variants = [...(inner.options as z.ZodObject<z.ZodRawShape>[])].map((o) => {
      const shape = o.shape;
      const disc = inner.discriminator as string;
      const value = (shape[disc] as z.ZodLiteral<string>).value;
      return {
        value,
        fields: Object.entries(shape)
          .filter(([k]) => k !== disc)
          .map(([k, s]) => describe(k, s as AnyZod)),
      };
    });
    return { ...base, kind: 'variant', variants };
  }
  if (inner instanceof z.ZodObject) {
    const shape = inner.shape as Record<string, AnyZod>;
    const keys = Object.keys(shape);
    if (keys.length === 2 && keys.includes('x') && keys.includes('y'))
      return { ...base, kind: 'point' };
    return {
      ...base,
      kind: 'object',
      fields: keys.map((k) => describe(k, shape[k]!)),
    };
  }
  if (inner instanceof z.ZodArray) {
    const item = describe(key, inner.element as AnyZod);
    const len = inner._def;
    return {
      ...base,
      kind: 'list',
      item: { ...item, optional: false },
      ...(len.minLength ? { min: len.minLength.value } : {}),
      ...(len.maxLength ? { max: len.maxLength.value } : {}),
    };
  }
  throw new Error(`champ « ${key} » : forme non prise en charge par le Studio`);
}

function fieldsOfObjectSchema(schema: z.ZodObject<z.ZodRawShape>): FieldSpec[] {
  return Object.entries(schema.shape)
    .filter(([k]) => !HIDDEN.has(k))
    .map(([k, s]) => describe(k, s as AnyZod));
}

const objectFieldCache = new Map<string, FieldSpec[]>();
/** Champs d'un objet du type donné (sans `id` ni `type`). */
export function objectFields(type: JrafObject['type']): FieldSpec[] {
  let f = objectFieldCache.get(type);
  if (!f) {
    const schema = jrafObjectSchema.optionsMap.get(type) as z.ZodObject<z.ZodRawShape> | undefined;
    if (!schema) throw new Error(`type d'objet inconnu : ${type}`);
    f = fieldsOfObjectSchema(schema);
    objectFieldCache.set(type, f);
  }
  return f;
}

const stepFieldCache = new Map<string, FieldSpec[]>();
/** Champs d'une étape de l'action donnée (sans `id` ni `action`). */
export function stepFields(action: JrafAction): FieldSpec[] {
  let f = stepFieldCache.get(action);
  if (!f) {
    const schema = jrafStepSchema.optionsMap.get(action) as z.ZodObject<z.ZodRawShape> | undefined;
    if (!schema) throw new Error(`action inconnue : ${action}`);
    f = fieldsOfObjectSchema(schema);
    stepFieldCache.set(action, f);
  }
  return f;
}

/** Libellés des types d'objets et des actions (Studio). */
export const OBJECT_TYPE_LABELS: Record<JrafObject['type'], string> = {
  rect: 'Rectangle',
  circle: 'Cercle',
  ellipse: 'Ellipse',
  line: 'Segment',
  arrow: 'Flèche',
  polygon: 'Polygone',
  path: 'Tracé',
  point: 'Point',
  text: 'Texte',
  formula: 'Formule',
  value: 'Nombre',
  image: 'Image',
  group: 'Groupe',
};

export const ACTION_LABELS: Record<JrafAction, string> = {
  'object.show': 'Montrer',
  'object.hide': 'Cacher',
  'object.move': 'Déplacer',
  'object.scale': 'Agrandir / réduire',
  'object.rotate': 'Tourner',
  'object.style': 'Changer le style',
  'label.set': 'Changer le texte',
  'formula.transform': 'Transformer la formule',
  'value.set': 'Changer le nombre',
  'object.highlight': 'Éclairer',
  'timeline.wait': 'Attendre',
  'question.ask': 'Question (QCM)',
  'tool.open': 'Ouvrir un outil',
};
